import { msg } from "@lingui/core/macro";
import {
  closeRegisterSessionMutation,
  formatMoney,
  getOpenSessionQuery,
  openRegisterSessionMutation,
  parseMoneyToCents,
  queryKeys,
} from "@comandero/shared";
import type { Branch, Organization, RegisterSession } from "@comandero/powersync";
import { createPowerSyncQuery } from "@comandero/shared/solid";
import { createMemo, createSignal, Show } from "solid-js";
import { History } from "lucide-solid";
import { Button } from "@/components/ui/button";
import { Link } from "@/lib/u-router";
import { TextField } from "@/components/ui/text-field";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";

interface CashboxManagerProps {
  org: () => Organization | null;
  branch: () => Branch | null;
}

export function CashboxManager(props: CashboxManagerProps) {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const { db } = usePowerSyncContext();

  const [openingFloat, setOpeningFloat] = createSignal("");
  const [countedTotal, setCountedTotal] = createSignal("");
  const [variance, setVariance] = createSignal<number | null>(null);
  const [expected, setExpected] = createSignal<number | null>(null);
  // Turno recién cerrado: para enlazar su resumen de actividad en el cuadre.
  const [lastClosedSessionId, setLastClosedSessionId] = createSignal<string | null>(null);

  const orgId = (): string | null => props.org()?.id ?? null;
  const branchId = (): string | null => props.branch()?.id ?? null;

  const openSession = createPowerSyncQuery<RegisterSession>({
    queryKey: () => queryKeys.comandero.openSession(branchId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const oid = orgId();
      const bid = branchId();
      return d && oid && bid ? getOpenSessionQuery(d, oid, bid) : null;
    },
    db,
    enabled: () => Boolean(branchId()),
  });

  const session = createMemo<RegisterSession | null>(() => openSession.data?.[0] ?? null);

  async function open(e: Event) {
    e.preventDefault();
    const d = drizzle();
    const oid = orgId();
    const bid = branchId();
    if (!d || !oid || !bid) {
      return;
    }
    await openRegisterSessionMutation(d, oid, bid, parseMoneyToCents(openingFloat()));
    setOpeningFloat("");
    setVariance(null);
    setExpected(null);
  }

  async function close(e: Event) {
    e.preventDefault();
    const d = drizzle();
    const oid = orgId();
    const s = session();
    if (!d || !oid || !s?.id) {
      return;
    }
    const result = await closeRegisterSessionMutation(
      d,
      oid,
      s.id,
      parseMoneyToCents(countedTotal()),
    );
    setVariance(result.variance);
    setExpected(result.expected);
    setLastClosedSessionId(s.id);
    setCountedTotal("");
  }

  return (
    <div class="flex flex-col gap-6" data-testid="cashbox-manager">
      <Show
        when={session()}
        fallback={
          <form class="flex items-end gap-2" onSubmit={open}>
            <div class="flex flex-col gap-1">
              <label class="text-xs text-muted-foreground" for="opening-float">
                {t(msg`Opening float`)}
              </label>
              <TextField
                id="opening-float"
                data-testid="opening-float-input"
                placeholder="0.00"
                inputmode="decimal"
                value={openingFloat()}
                onInput={(e) => setOpeningFloat(e.currentTarget.value)}
              />
            </div>
            <Button data-testid="open-session-btn" type="submit">
              {t(msg`Open register`)}
            </Button>
          </form>
        }
      >
        <div class="rounded-md border border-border/40 p-4" data-testid="open-session-card">
          <div class="text-sm text-muted-foreground">{t(msg`Register open`)}</div>
          <div class="text-sm">
            {t(msg`Opening float`)}:{" "}
            <span data-testid="session-float">{formatMoney(session()?.opening_float)}</span>
          </div>
          <form class="mt-3 flex items-end gap-2" onSubmit={close}>
            <div class="flex flex-col gap-1">
              <label class="text-xs text-muted-foreground" for="counted-total">
                {t(msg`Counted cash`)}
              </label>
              <TextField
                id="counted-total"
                data-testid="counted-total-input"
                placeholder="0.00"
                inputmode="decimal"
                value={countedTotal()}
                onInput={(e) => setCountedTotal(e.currentTarget.value)}
              />
            </div>
            <Button data-testid="close-session-btn" type="submit">
              {t(msg`Close & reconcile`)}
            </Button>
          </form>
          {/* ACTIVITY-CORE: vista "actividad del turno" para el cuadre. */}
          <Link
            to="/activity"
            search={{ session: session()?.id }}
            class="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
            data-testid="shift-activity-link"
          >
            <History class="h-3.5 w-3.5" />
            {t(msg`View shift activity`)}
          </Link>
        </div>
      </Show>

      <Show when={variance() !== null}>
        <div class="rounded-md border border-border/40 p-4 text-sm" data-testid="reconciliation">
          <div>
            {t(msg`Expected`)}: <span data-testid="expected-total">{formatMoney(expected())}</span>
          </div>
          <div>
            {t(msg`Variance`)}: <span data-testid="variance-total">{formatMoney(variance())}</span>
          </div>
          <Show when={lastClosedSessionId()}>
            {(sid) => (
              <Link
                to="/activity"
                search={{ session: sid() }}
                class="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
                data-testid="closed-shift-activity-link"
              >
                <History class="h-3.5 w-3.5" />
                {t(msg`View shift activity (voids, discounts, reopens)`)}
              </Link>
            )}
          </Show>
        </div>
      </Show>
    </div>
  );
}
